import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Class, ClassInput, DeliveryMode, ProgramType, Weekday } from '@pirouette/shared';
import { classInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';
import { queryKeys } from '@/lib/queryClient';

export interface ClassListQuery {
  termId?: string | undefined;
  weekday?: Weekday | undefined;
  programId?: string | undefined;
  locationId?: string | undefined;
  deliveryMode?: DeliveryMode | undefined;
  programType?: ProgramType | undefined;
  search?: string | undefined;
}

export function useClasses(query: ClassListQuery = {}) {
  return useQuery({
    queryKey: [...queryKeys.classes, query],
    queryFn: async (): Promise<Class[]> => {
      const selectColumns = query.programType
        ? '*, program_offerings!inner(id, name, program_type), studio_locations(id, name, kind)'
        : '*, program_offerings(id, name, program_type), studio_locations(id, name, kind)';
      let q = supabase
        .from('classes')
        .select(selectColumns)
        .is('deleted_at', null)
        .order('weekday', { ascending: true })
        .order('start_time', { ascending: true });

      if (query.termId) q = q.eq('term_id', query.termId);
      if (query.weekday) q = q.eq('weekday', query.weekday);
      if (query.programId) q = q.eq('program_id', query.programId);
      if (query.locationId) q = q.eq('location_id', query.locationId);
      if (query.deliveryMode) q = q.eq('delivery_mode', query.deliveryMode);
      if (query.programType) q = q.eq('program_offerings.program_type', query.programType);
      if (query.search) {
        const term = `%${query.search.trim()}%`;
        q = q.or(`name.ilike.${term},style.ilike.${term},room.ilike.${term}`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Class[];
    },
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClassInput): Promise<Class> => {
      const parsed = classInputSchema.parse(input);
      const { data, error } = await supabase
        .from('classes')
        .insert(stripUndefined({ ...parsed, program_type: undefined }))
        .select('*')
        .single();
      if (error) throw error;
      return data as Class;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.classes });
    },
  });
}
